import Dexie from 'dexie';

import { ApiError } from '../api/errors';

import { applyBootstrap, ensureTemplatePackages, type PackageSummary } from './bootstrap';
import {
    subscribeToNetwork as defaultSubscribeToNetwork,
    subscribeToWake as defaultSubscribeToWake,
    isOnline as defaultIsOnline,
} from './network';
import { backoffDelay, pendingOperationCount, takeBatch } from './outbox';
import {
    deletePhoto,
    duePhotoDeletions,
    duePhotoUploads,
    pendingPhotoCount,
    uploadPhoto,
} from './photo-upload';
import { applyPull } from './pull';
import { failBatch, isUnreachable, settleBatch, settledGeneration } from './push';
import { SYNC_META_KEYS } from './records';
import type { SyncApi, SyncRequestOptions } from './sync-api';
import type { TenantDatabase } from './tenant-db';

export type SyncStatus = 'idle' | 'syncing' | 'pending' | 'offline' | 'error';

export interface SyncState {
    status: SyncStatus;
    online: boolean;
    /** Bewerkingen in de outbox plus foto's die nog geüpload moeten worden. */
    pendingCount: number;
    /** Conflicten en weigeringen die de inspecteur nog niet gezien heeft. */
    conflictCount: number;
    lastSyncedAt: number | null;
    lastError: string | null;
}

export const INITIAL_SYNC_STATE: SyncState = {
    status: 'idle',
    online: true,
    pendingCount: 0,
    conflictCount: 0,
    lastSyncedAt: null,
    lastError: null,
};

export interface SyncSummary {
    skipped: 'offline' | null;
    bootstrapped: boolean;
    batches: number;
    applied: number;
    conflicts: number;
    rejected: number;
    pulledPages: number;
    packages: PackageSummary | null;
    photosUploaded: number;
    photosDeleted: number;
    error: string | null;
}

export interface SyncEngineOptions {
    db: TenantDatabase;
    api: SyncApi;
    /** `session.device.uuid`; zonder apparaat wordt er niet gepusht. */
    deviceUuid: () => string | null;
    isOnline?: () => boolean;
    now?: () => number;
    /** Hoe vaak de engine uit zichzelf synchroniseert, in ms. 0 = nooit. */
    pollIntervalMs?: number;
    /**
     * Na een wijziging wacht de engine zo lang op een volgende tik voordat hij
     * verstuurt (`syncSoon`), in ms. Standaard 2 s.
     */
    flushDelayMs?: number;
    /** Maar nooit langer dan dit na de eerste wijziging die nog wacht, in ms. Standaard 10 s. */
    maxFlushDelayMs?: number;
    subscribeToNetwork?: (listener: (online: boolean) => void) => () => void;
    /** Het tabblad of de laptop komt terug na een pauze: zie `subscribeToWake`. */
    subscribeToWake?: (listener: () => void) => () => void;
    /**
     * Zo lang mag een lopende ronde zonder antwoord op enig verzoek blijven
     * voordat "Nu versturen" hem afbreekt, in ms. Standaard 15 s.
     */
    stallAfterMs?: number;
}

/** Een ronde die door de engine zelf is afgebroken; geen fout en geen mislukte poging. */
class SyncAborted extends Error {
    constructor() {
        super('De synchronisatie is afgebroken.');
        this.name = 'SyncAborted';
    }
}

/** Een veiligheidsgrens: meer pagina's in één ronde wijst op een cursor die niet opschuift. */
const MAX_PULL_PAGES = 500;
const MAX_BATCHES_PER_ROUND = 50;

/**
 * De sync-engine van één omgeving. Eén ronde:
 *
 * 1. **push** - de outbox in batches van hoogstens 200 naar `POST /sync/push`,
 *    met `Idempotency-Key = batch_uuid` en `device_uuid` uit de sessie;
 * 2. **bootstrap** (na inloggen, bij opstarten, na een wissel, of zonder cursor)
 *    of anders **pull** tot `has_more = false`;
 * 3. de aangekondigde **templatepakketten** ophalen;
 * 4. **foto's**, als laatste en alleen bij een antwoord dat de server kent.
 *
 * Netwerkfouten stoppen de ronde; de engine probeert het opnieuw met
 * exponentiële backoff, bij het online-event en periodiek. Offline is een
 * normale toestand, geen fout.
 *
 * Een ronde kan niet blijven hangen. Elk verzoek heeft een tijdslimiet, en
 * "Nu versturen" (`retryNow`) breekt een ronde af die al `stallAfterMs` geen
 * antwoord meer kreeg, zet de backoff opzij en begint opnieuw. Hetzelfde
 * gebeurt als de verbinding terugkomt, als het tabblad na een pauze weer in
 * beeld komt, en als de klok een sprong maakt (de laptop sliep).
 *
 * De engine is vrij van React: tests sturen hem met een eigen `fetch`.
 */
export class SyncEngine {
    private readonly options: SyncEngineOptions;
    private state: SyncState;
    private readonly listeners = new Set<(state: SyncState) => void>();
    private running: Promise<SyncSummary> | null = null;
    private rerun = false;
    private bootstrapRequested = false;
    private failures = 0;
    private wakeUp: ReturnType<typeof setTimeout> | null = null;
    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private flushDeadline: number | null = null;
    private stopHandlers: Array<() => void> = [];
    private stopped = false;
    private runAbort: AbortController | null = null;
    /** Wanneer de lopende ronde voor het laatst iets terughoorde (`Date.now()`). */
    private lastProgressAt = 0;

    constructor(options: SyncEngineOptions) {
        this.options = options;
        this.state = { ...INITIAL_SYNC_STATE, online: this.online() };
    }

    get db(): TenantDatabase {
        return this.options.db;
    }

    getState = (): SyncState => this.state;

    subscribe = (listener: (state: SyncState) => void): (() => void) => {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    };

    /** Luistert naar het netwerk en synchroniseert periodiek. De eerste ronde start `requestBootstrap`. */
    start(): () => void {
        this.stopped = false;
        const subscribe = this.options.subscribeToNetwork ?? defaultSubscribeToNetwork;

        this.stopHandlers.push(
            subscribe((online) => {
                this.patch({ online, ...(online ? {} : { status: 'offline' }) });

                /* De verbinding is terug: wat in de backoff stond, hoeft niet langer te wachten. */
                if (online) {
                    void this.retryNow();
                }
            }),
        );
        this.stopHandlers.push(
            (this.options.subscribeToWake ?? defaultSubscribeToWake)(() => void this.retryNow()),
        );

        const interval = this.options.pollIntervalMs ?? 30_000;

        if (interval > 0) {
            let lastTick = Date.now();
            const timer = setInterval(() => {
                const gap = Date.now() - lastTick;
                lastTick = Date.now();

                /*
                 * Een tik die veel te laat komt: de laptop sliep. Een verzoek van
                 * daarvoor antwoordt misschien nooit meer.
                 */
                void (gap > interval * 3 ? this.retryNow() : this.sync());
            }, interval);
            this.stopHandlers.push(() => clearInterval(timer));
        }

        void this.refreshCounters();

        return () => this.stop();
    }

    stop(): void {
        this.stopped = true;
        this.stopHandlers.forEach((stop) => stop());
        this.stopHandlers = [];
        this.clearWakeUp();
        this.clearFlush();
        this.runAbort?.abort();
    }

    /** Na inloggen, bij opstarten en na wisselen van omgeving: alles opnieuw van de server. */
    requestBootstrap(): Promise<SyncSummary> {
        this.bootstrapRequested = true;

        return this.sync();
    }

    /**
     * Eén ronde. Een aanroep tijdens een lopende ronde levert die ronde op en
     * plant er één achteraan, zodat een wijziging van net nooit blijft liggen.
     */
    sync(): Promise<SyncSummary> {
        if (this.running) {
            this.rerun = true;

            return this.running;
        }

        this.running = this.run().finally(() => {
            this.running = null;
            this.runAbort = null;

            if (this.rerun && !this.stopped) {
                this.rerun = false;
                void this.sync();
            }
        });

        return this.running;
    }

    /**
     * Na een wijziging: synchroniseer zodra de inspecteur even niet tikt, en
     * uiterlijk `maxFlushDelayMs` na de eerste wijziging die wacht.
     *
     * Elke tik een eigen ronde (push, pull en een herberekening op de server)
     * haalde bij vlot tikken de verzoekenlimiet van de API (120 per minuut per
     * persoon): daarna weigerde de server ook het indienen en de foto's. De
     * wijzigingen staan intussen gewoon in de outbox; wachten kost niets.
     */
    syncSoon(): void {
        if (this.stopped || typeof setTimeout !== 'function') {
            return;
        }

        const delay = this.options.flushDelayMs ?? 2_000;
        const maxDelay = this.options.maxFlushDelayMs ?? 10_000;
        const now = Date.now();

        this.flushDeadline ??= now + maxDelay;

        if (this.flushTimer !== null) {
            clearTimeout(this.flushTimer);
        }

        this.flushTimer = setTimeout(
            () => {
                this.clearFlush();
                void this.sync();
            },
            Math.max(0, Math.min(delay, this.flushDeadline - now)),
        );
    }

    /** Alias voor de knop "nu versturen". */
    flush(): Promise<SyncSummary> {
        return this.retryNow();
    }

    /**
     * "Nu versturen": een echte nieuwe poging.
     *
     * - Een lopende ronde die al `stallAfterMs` niets terughoorde, wordt
     *   afgebroken; een ronde die gewoon bezig is, mag afmaken.
     * - Daarna mag alles wat in de backoff staat meteen opnieuw.
     *
     * Vroeger leverde de knop tijdens een ronde die ronde zelf op, en sloeg hij
     * de backoff niet over: een foto die na een netwerkfout vijf minuten moest
     * wachten, bleef dat doen, hoe vaak je ook drukte.
     */
    async retryNow(): Promise<SyncSummary> {
        const current = this.running;

        if (current) {
            if (Date.now() - this.lastProgressAt >= (this.options.stallAfterMs ?? 15_000)) {
                this.runAbort?.abort();
            }

            await current.catch(() => undefined);
        }

        try {
            await this.skipBackoff();
        } catch (error) {
            if (!isClosed(error)) {
                throw error;
            }
        }

        return this.sync();
    }

    private async skipBackoff(): Promise<void> {
        const now = this.now();

        await this.db.transaction('rw', this.db.batches, this.db.photos, async () => {
            await this.db.batches.toCollection().modify({ nextAttemptAt: now });
            await this.db.photos
                .filter(
                    (photo) =>
                        photo.deleted === 1 ||
                        photo.state === 'pending' ||
                        photo.state === 'uploading',
                )
                .modify({ nextAttemptAt: now });
        });
        this.failures = 0;
    }

    async refreshCounters(): Promise<SyncState> {
        try {
            const [operations, photos, conflicts, synced] = await Promise.all([
                pendingOperationCount(this.db),
                pendingPhotoCount(this.db),
                /* Een melding is geen fout: die staat boven de vragen, niet in rood in de balk. */
                this.db.conflicts
                    .where('acknowledged')
                    .equals(0)
                    .filter((conflict) => conflict.kind !== 'notice')
                    .count(),
                this.db.syncMeta.get(SYNC_META_KEYS.lastSyncedAt),
            ]);
            const pendingCount = operations + photos;
            const online = this.online();
            const status: SyncStatus = !online
                ? 'offline'
                : conflicts > 0
                  ? 'error'
                  : pendingCount > 0
                    ? 'pending'
                    : 'idle';

            /* Na een herstart weet de engine nog wanneer er voor het laatst gesynchroniseerd is. */
            const lastSyncedAt =
                this.state.lastSyncedAt ??
                (typeof synced?.value === 'number' ? synced.value : null);

            this.patch({ pendingCount, conflictCount: conflicts, online, status, lastSyncedAt });
        } catch (error) {
            if (!isClosed(error)) {
                throw error;
            }
        }

        return this.state;
    }

    private async run(): Promise<SyncSummary> {
        const summary: SyncSummary = {
            skipped: null,
            bootstrapped: false,
            batches: 0,
            applied: 0,
            conflicts: 0,
            rejected: 0,
            pulledPages: 0,
            packages: null,
            photosUploaded: 0,
            photosDeleted: 0,
            error: null,
        };

        if (this.stopped) {
            return summary;
        }

        if (!this.online()) {
            this.patch({ online: false, status: 'offline' });
            await this.refreshCounters();

            return { ...summary, skipped: 'offline' };
        }

        this.patch({ online: true, status: 'syncing' });

        const abort = new AbortController();
        this.runAbort = abort;
        this.lastProgressAt = Date.now();
        const api = this.tracked(abort.signal);

        try {
            const pushed = await this.push(summary, api, abort.signal);

            if (pushed !== 'unreachable') {
                /*
                 * Een pull die misgaat (een fout op de server, een antwoord dat niet
                 * klopt) houdt de foto's niet tegen: die hebben alleen hun antwoord
                 * op de server nodig, en dat is net gepusht. Alleen zonder
                 * verbinding heeft de rest van de ronde geen zin.
                 */
                let pullFailure: unknown = null;

                try {
                    await this.bootstrapOrPull(summary, api);
                    summary.packages = await ensureTemplatePackages(this.db, api, () => this.now());
                } catch (error) {
                    if (isClosed(error) || abort.signal.aborted || isNetworkFailure(error)) {
                        throw error;
                    }

                    pullFailure = error;
                }

                await this.uploadPhotos(summary, api, abort.signal);

                if (pullFailure !== null) {
                    throw pullFailure;
                }
            }

            if (pushed === 'unreachable') {
                throw new Error('De server is niet bereikbaar.');
            }

            this.failures = 0;
            const syncedAt = this.now();
            await this.db.syncMeta.put({ key: SYNC_META_KEYS.lastSyncedAt, value: syncedAt });
            this.patch({ lastSyncedAt: syncedAt, lastError: summary.error });
        } catch (error) {
            if (isClosed(error)) {
                return summary;
            }

            /* Afgebroken om opnieuw te beginnen: geen fout, en de volgende ronde wacht niet. */
            if (abort.signal.aborted || error instanceof SyncAborted) {
                await this.refreshCounters();

                return summary;
            }

            this.failures += 1;
            summary.error = error instanceof Error ? error.message : String(error);
            this.patch({ lastError: summary.error });
            this.scheduleWakeUp(backoffDelay(this.failures));
        }

        await this.refreshCounters();
        await this.scheduleNextAttempt();

        return summary;
    }

    /**
     * De API voor deze ronde: elk verzoek draagt het signaal van de ronde, en elk
     * antwoord (ook een fout) telt als teken van leven.
     */
    private tracked(signal: AbortSignal): SyncApi {
        const api = this.options.api;
        const request: SyncRequestOptions = { signal };
        const heard = <T>(promise: Promise<T>): Promise<T> =>
            promise.finally(() => {
                this.lastProgressAt = Date.now();
            });

        return {
            bootstrap: () => heard(api.bootstrap(request)),
            pull: (cursor) => heard(api.pull(cursor, request)),
            push: (body) => heard(api.push(body, request)),
            fetchPackage: (input) => heard(api.fetchPackage(input, request)),
            uploadMedia: (input) => heard(api.uploadMedia(input, request)),
            deleteMedia: (mediaUuid) => heard(api.deleteMedia(mediaUuid, request)),
        };
    }

    private async push(
        summary: SyncSummary,
        api: SyncApi,
        signal: AbortSignal,
    ): Promise<'done' | 'waiting' | 'unreachable'> {
        const deviceUuid = this.options.deviceUuid();

        if (!deviceUuid) {
            return 'waiting';
        }

        for (let round = 0; round < MAX_BATCHES_PER_ROUND; round += 1) {
            const batch = await takeBatch(this.db, deviceUuid, this.now());

            if (!batch) {
                return 'done';
            }

            if (batch.nextAttemptAt > this.now()) {
                return 'waiting';
            }

            try {
                const response = await api.push(batch.body);
                const settled = await settleBatch(this.db, batch, response, this.now());

                summary.batches += 1;
                summary.applied += settled.applied;
                summary.conflicts += settled.conflicts;
                summary.rejected += settled.rejected;
            } catch (error) {
                if (isClosed(error)) {
                    throw error;
                }

                /* Dezelfde batch gaat in de volgende ronde ongewijzigd opnieuw: geen strafpunt. */
                if (signal.aborted) {
                    throw new SyncAborted();
                }

                const failure = await failBatch(this.db, batch, error, this.now());

                if (failure === 'retry') {
                    /* De batch wacht met backoff; pull en foto's gaan gewoon door. */
                    summary.error = error instanceof Error ? error.message : String(error);

                    return isUnreachable(error) ? 'unreachable' : 'waiting';
                }
            }
        }

        return 'done';
    }

    private async bootstrapOrPull(summary: SyncSummary, api: SyncApi): Promise<void> {
        const cursor = (await this.db.syncMeta.get(SYNC_META_KEYS.cursor))?.value;

        if (this.bootstrapRequested || typeof cursor !== 'string') {
            /*
             * Verliet er onderweg een bewerking de outbox (een tweede tabblad dat
             * verstuurde), dan kan de bootstrap ouder zijn dan wat hier staat: dan
             * nog een keer, en anders volgende ronde.
             */
            for (let attempt = 0; attempt < 2; attempt += 1) {
                const settled = await settledGeneration(this.db);
                const data = await api.bootstrap();

                if (await applyBootstrap(this.db, data, this.now(), settled)) {
                    this.bootstrapRequested = false;
                    summary.bootstrapped = true;

                    return;
                }
            }

            return;
        }

        let next = cursor;
        let stale = 0;

        for (let page = 0; page < MAX_PULL_PAGES; page += 1) {
            let data: Awaited<ReturnType<SyncApi['pull']>>;
            const settled = await settledGeneration(this.db);

            try {
                data = await api.pull(next);
            } catch (error) {
                /*
                 * `422`: de cursor hoort niet (meer) bij deze omgeving. Dan
                 * opnieuw beginnen met een bootstrap, in dezelfde ronde.
                 */
                if (!(error instanceof ApiError) || error.status !== 422) {
                    throw error;
                }

                await this.db.syncMeta.delete(SYNC_META_KEYS.cursor);
                this.bootstrapRequested = true;
                await this.bootstrapOrPull(summary, api);

                return;
            }

            const applied = await applyPull(this.db, data, this.now(), settled);

            /* Ouder dan wat hier staat: dezelfde pagina nog eens ophalen. */
            if (applied.stale) {
                stale += 1;

                if (stale > 3) {
                    return;
                }

                continue;
            }

            summary.pulledPages += 1;

            if (!data.has_more || data.cursor === next) {
                return;
            }

            next = data.cursor;
        }
    }

    private async uploadPhotos(
        summary: SyncSummary,
        api: SyncApi,
        signal: AbortSignal,
    ): Promise<void> {
        for (const due of await duePhotoUploads(this.db, this.now())) {
            const outcome = await uploadPhoto(this.db, api, due, () => this.now(), { signal });

            if (outcome === 'uploaded') {
                summary.photosUploaded += 1;
            }

            if (outcome === 'unreachable' || outcome === 'aborted') {
                return;
            }
        }

        for (const photo of await duePhotoDeletions(this.db, this.now())) {
            const outcome = await deletePhoto(this.db, api, photo, () => this.now(), { signal });

            if (outcome === 'deleted') {
                summary.photosDeleted += 1;
            }

            if (outcome === 'unreachable' || outcome === 'aborted') {
                return;
            }
        }
    }

    /** Wordt wakker zodra de eerstvolgende batch of foto weer aan de beurt is. */
    private async scheduleNextAttempt(): Promise<void> {
        if (this.stopped) {
            return;
        }

        try {
            const now = this.now();
            const moments = [
                ...(await this.db.batches.toArray()).map((batch) => batch.nextAttemptAt),
                ...(await this.db.photos.toArray())
                    .filter(
                        (photo) =>
                            (photo.blob !== null && photo.state === 'pending') ||
                            photo.deleted === 1,
                    )
                    .map((photo) => photo.nextAttemptAt),
            ].filter((moment) => moment > now);

            if (moments.length > 0) {
                this.scheduleWakeUp(Math.min(...moments) - now);
            }
        } catch (error) {
            if (!isClosed(error)) {
                throw error;
            }
        }
    }

    private scheduleWakeUp(delayMs: number): void {
        if (this.stopped || typeof setTimeout !== 'function') {
            return;
        }

        this.clearWakeUp();
        this.wakeUp = setTimeout(() => {
            this.wakeUp = null;
            void this.sync();
        }, delayMs);
    }

    private clearFlush(): void {
        if (this.flushTimer !== null) {
            clearTimeout(this.flushTimer);
        }

        this.flushTimer = null;
        this.flushDeadline = null;
    }

    private clearWakeUp(): void {
        if (this.wakeUp !== null) {
            clearTimeout(this.wakeUp);
            this.wakeUp = null;
        }
    }

    private patch(next: Partial<SyncState>): void {
        const merged = { ...this.state, ...next };
        const changed = (Object.keys(merged) as Array<keyof SyncState>).some(
            (key) => merged[key] !== this.state[key],
        );

        if (!changed) {
            return;
        }

        this.state = merged;
        this.listeners.forEach((listener) => listener(this.state));
    }

    private online(): boolean {
        return (this.options.isOnline ?? defaultIsOnline)();
    }

    private now(): number {
        return (this.options.now ?? Date.now)();
    }
}

function isClosed(error: unknown): boolean {
    return error instanceof Dexie.DatabaseClosedError;
}

/** Geen verbinding, of de server antwoordde niet op tijd. */
function isNetworkFailure(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        (error.kind === 'offline' || error.kind === 'network' || error.kind === 'timeout')
    );
}

/**
 * Kleurtoon van de sync-indicator. Offline is nadrukkelijk *niet* rood: een
 * inspecteur in een kruipruimte doet niets fout.
 */
export function syncTone(state: SyncState): 'neutral' | 'amber' | 'red' {
    if (state.conflictCount > 0) {
        return 'red';
    }

    if (!state.online || state.pendingCount > 0) {
        return 'amber';
    }

    return 'neutral';
}

/** Korte Nederlandse omschrijving voor de statusregel. */
export function syncLabel(state: SyncState): string {
    if (state.conflictCount > 0) {
        return `${state.conflictCount} ${state.conflictCount === 1 ? 'wijziging' : 'wijzigingen'} niet overgenomen`;
    }

    if (!state.online) {
        if (state.pendingCount > 0) {
            return `Offline · ${state.pendingCount} ${state.pendingCount === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} op verzending`;
        }

        return state.lastSyncedAt === null
            ? 'Offline · nog niet gesynchroniseerd'
            : 'Offline · alles staat lokaal klaar';
    }

    if (state.status === 'syncing') {
        return 'Bezig met synchroniseren';
    }

    if (state.pendingCount > 0) {
        return `${state.pendingCount} ${state.pendingCount === 1 ? 'wijziging wacht' : 'wijzigingen wachten'}`;
    }

    /* Nooit "alles gesynchroniseerd" beweren voordat er één keer met de server is gesproken. */
    return state.lastSyncedAt === null ? 'Nog niet gesynchroniseerd' : 'Alles gesynchroniseerd';
}
